import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import CustomInput from "../../../components/CustomInput";
import CustomSelect from "../../../components/CustomSelect";

const TabGeneralInfo = ({
  formData,
  onInputChange,
  errors,
  departmentOptions,
  staffOptions,
  loadMoreDepartments,
  deptHasMore,
  deptLoadingMore,
  onDeptSearch,
  onDeptSearchSubmit,
  onDeptDropdownOpen,
  loadMoreStaff,
  staffHasMore,
  staffLoadingMore,
  onStaffSearch,
  onStaffSearchSubmit,
  onStaffDropdownOpen,
  supervisorOptions,
  loadMoreSupervisors,
  supervisorHasMore,
  supervisorLoadingMore,
  onSupervisorSearch,
  onSupervisorSearchSubmit,
  onSupervisorDropdownOpen,
  canEditDept = false,
  canEditStaff = false,
}) => {
  const [imgSrc, setImgSrc] = useState(formData.avatarUrl || null);

  useEffect(() => {
    if (!formData.avatar && formData.avatarUrl) {
      setImgSrc(formData.avatarUrl);
    }
  }, [formData.avatarUrl, formData.avatar]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Chỉ chấp nhận định dạng JPEG/PNG.");
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error("Kích thước tệp vượt quá 25MB.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setImgSrc(reader.result);
    reader.readAsDataURL(file);

    onInputChange("avatar", file);
  };

  return (
    <form className="space-y-4">
      <h2 className="text-md font-sf-medium text-gray-900 mb-2">
        Thông tin chung
      </h2>

      <div className="flex items-center">
        <label
          htmlFor="image-upload"
          className="cursor-pointer w-28 h-28 bg-gray-100 flex items-center justify-center rounded-md overflow-hidden relative hover:opacity-75"
        >
          {imgSrc ? (
            <img
              src={imgSrc}
              alt="Avatar"
              className="w-full h-full object-cover"
            />
          ) : (
            <img src="/icons/camera-icon.svg" alt="Camera icon" />
          )}

          <input
            id="image-upload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageChange}
          />
        </label>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm mb-1">Phòng ban phụ trách</label>
          <CustomSelect
            options={departmentOptions}
            placeholder="Chọn phòng ban"
            value={formData.responsibleDepartment}
            onChange={(opt) => onInputChange("responsibleDepartment", opt)}
            searchable
            infinite
            loadMore={loadMoreDepartments}
            hasMore={deptHasMore}
            loadingMore={deptLoadingMore}
            onSearch={onDeptSearch}
            onSearchSubmit={onDeptSearchSubmit}
            onDropdownOpen={onDeptDropdownOpen}
            disabled={!canEditDept}
          />
        </div>

        <div>
          <label className="block text-sm mb-1">Nhân viên phụ trách</label>
          <CustomSelect
            options={staffOptions}
            placeholder="Chọn nhân viên"
            value={formData.responsibleStaff}
            onChange={(opt) => onInputChange("responsibleStaff", opt)}
            searchable
            infinite
            loadMore={loadMoreStaff}
            hasMore={staffHasMore}
            loadingMore={staffLoadingMore}
            onSearch={onStaffSearch}
            onSearchSubmit={onStaffSearchSubmit}
            onDropdownOpen={onStaffDropdownOpen}
            disabled={!canEditStaff}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block text-sm mb-1">Giám sát viên</label>
          <CustomSelect
            options={supervisorOptions}
            placeholder="Chọn giám sát viên"
            value={formData.supervisors}
            onChange={(opt) => onInputChange("supervisors", opt)}
            isMulti={true}
            searchable
            infinite
            loadMore={loadMoreSupervisors}
            hasMore={supervisorHasMore}
            loadingMore={supervisorLoadingMore}
            onSearch={onSupervisorSearch}
            onSearchSubmit={onSupervisorSearchSubmit}
            onDropdownOpen={onSupervisorDropdownOpen}
            disabled={!canEditStaff}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <CustomInput
          placeholder="Tên công ty/tổ chức"
          required={true}
          value={formData.companyName ?? ""}
          error={errors?.companyName}
          onChange={(value) => onInputChange("companyName", value)}
        />
        <CustomInput
          placeholder="Người đại diện"
          required={true}
          value={formData.fullName || ""}
          error={errors?.fullName}
          onChange={(value) => onInputChange("fullName", value)}
        />
        <CustomInput
          placeholder="Email"
          type="email"
          required={true}
          value={formData.email || ""}
          error={errors?.email}
          onChange={(value) => onInputChange("email", value)}
        />
        <CustomInput
          placeholder="Số điện thoại"
          required={true}
          value={formData.phone || ""}
          error={errors?.phone}
          onChange={(value) => onInputChange("phone", value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <CustomInput
          placeholder="Website"
          value={formData.website || ""}
          onChange={(value) => onInputChange("website", value)}
        />
        <CustomInput
          placeholder="Mã số thuế"
          value={formData.MST || ""}
          onChange={(value) => onInputChange("MST", value)}
        />
      </div>

      <div className="grid grid-cols-4 gap-4">
        <CustomInput
          placeholder="Tỉnh/Thành phố"
          value={formData.city || ""}
          onChange={(value) => onInputChange("city", value)}
        />
        <CustomInput
          placeholder="Quận/Huyện"
          value={formData.district || ""}
          onChange={(value) => onInputChange("district", value)}
        />
        <div className="col-span-2">
          <CustomInput
            placeholder="Địa chỉ chi tiết"
            value={formData.detail || ""}
            onChange={(value) => onInputChange("detail", value)}
          />
        </div>
      </div>
    </form>
  );
};

export default TabGeneralInfo;
